import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { getAuthToken } from "../utils/getAuthToken";

const SERVER = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const POLL_MS = 30 * 1000;

/**
 * The open chat's WhatsApp Web state (docs/WHATSAPP_WEB_HANDOFF_PLAN.md, section 6): { linkId, opened, openedAt, expiresAt,
 * neverExpires, lastInboundPlatform? } while the contact has an active link, otherwise null.
 *
 * Read live from its own endpoint, not from the cached contact record (that one can be an hour old, and the contact may open the
 * link long after the agent loaded the chat). Refetched when the chat changes and every 30 s while it stays open.
 * `enabled` is false for chats that can never have a link (anything but a Meta WhatsApp contact), which then cost no requests.
 */
export default function useWhatsappWebStatus(contactId, enabled) {
  const workspaceId = useSelector((s) => s.workspace?.currentWorkspace?._id);
  const [status, setStatus] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [resetting, setResetting] = useState(false);

  const url = workspaceId && contactId ? `${SERVER}/api/workspaces/${workspaceId}/whatsapp-web/contacts/${contactId}` : null;

  const refresh = useCallback(async () => {
    if (!url) return;
    try {
      const token = await getAuthToken();
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data?.status || null);
    } catch (err) {
      // A failed lookup only hides the WhatsApp Web controls; the chat itself is unaffected.
      setStatus(null);
    }
  }, [url]);

  useEffect(() => {
    setStatus(null); // never show the previous chat's state while the next one loads
    if (!enabled || !url) return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, url, refresh]);

  const revoke = useCallback(async () => {
    if (!url) return;
    setRevoking(true);
    try {
      const token = await getAuthToken();
      await axios.post(`${url}/revoke`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(null);
      toast.success("WhatsApp Webchat link ended.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not end the WhatsApp Webchat link.");
    } finally {
      setRevoking(false);
    }
  }, [url]);

  // Makes the opened link usable again (same expiry); the contact's current browser chat ends.
  const reset = useCallback(async () => {
    if (!workspaceId || !status?.linkId) return;
    setResetting(true);
    try {
      const token = await getAuthToken();
      await axios.post(`${SERVER}/api/workspaces/${workspaceId}/whatsapp-web/links/${status.linkId}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      toast.success("WhatsApp Webchat link reset. The contact can open it again.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not reset the WhatsApp Webchat link.");
    } finally {
      setResetting(false);
    }
  }, [workspaceId, status?.linkId, refresh]);

  return { status, revoke, revoking, reset, resetting, refresh };
}
